import type { UserResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { AdminDraft } from '../../_shared/domain/administrator-draft.model';
import { validateDraft } from '../../_shared/domain/administrator-draft.validation';
import { toEditAdministratorError } from '../domain/edit-administrator-error';
import type { EditAdministratorGateway } from '../infrastructure/edit-administrator.gateway';
import {
  archiveToggleRequested,
  loadRequested,
  submitRequested,
} from './edit-administrator.actions';
import { selectDraft, selectUser } from './edit-administrator.selectors';
import { editAdministratorActions } from './edit-administrator.slice';

/**
 * WORKER: hydrate the admin being edited. A missing id resolves to a 404-shaped
 * business error so the page can redirect (mirrors the old page's behaviour). All
 * I/O goes through the injected gateway (read from the saga `context`).
 */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: EditAdministratorGateway = yield getContext('editAdministratorGateway');
  yield put(editAdministratorActions.loadStarted());
  try {
    const user = (yield call(gateway.loadAdministrator, action.payload)) as UserResponse | null;
    if (!user) {
      yield put(
        editAdministratorActions.loadFailed({
          kind: 'api',
          message: 'Administrator not found.',
          status: 404,
        }),
      );
      return;
    }
    yield put(editAdministratorActions.loadSucceeded(user));
  } catch (e) {
    yield put(editAdministratorActions.loadFailed(toEditAdministratorError(e)));
  }
}

/**
 * WORKER: persist the edited draft. Validates first (pure domain); on failure
 * dispatches a `validation` error and stops without touching the gateway.
 */
function* saveWorker() {
  const user = (yield select(selectUser)) as UserResponse | null;
  if (!user) return;
  const draft = (yield select(selectDraft)) as AdminDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(editAdministratorActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: EditAdministratorGateway = yield getContext('editAdministratorGateway');
  yield put(editAdministratorActions.saveStarted());
  try {
    yield call(gateway.updateAdministrator, user.id, draft);
    yield put(editAdministratorActions.saveSucceeded());
  } catch (e) {
    yield put(editAdministratorActions.saveFailed(toEditAdministratorError(e)));
  }
}

/** WORKER: archive / unarchive the loaded admin. */
function* archiveWorker(action: ReturnType<typeof archiveToggleRequested>) {
  const user = (yield select(selectUser)) as UserResponse | null;
  if (!user) return;
  const archived = action.payload;

  const gateway: EditAdministratorGateway = yield getContext('editAdministratorGateway');
  yield put(editAdministratorActions.archiveStarted());
  try {
    yield call(gateway.setArchived, user.id, archived);
    yield put(editAdministratorActions.archiveSucceeded(archived ? new Date().toISOString() : null));
  } catch (e) {
    yield put(editAdministratorActions.archiveFailed(toEditAdministratorError(e)));
  }
}

/** WATCHERS. */
export function* editAdministratorSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(submitRequested.type, saveWorker),
    takeLatest(archiveToggleRequested.type, archiveWorker),
  ]);
}
